import React, { useState, useCallback, useMemo } from 'react';
import {
  Typography,
  List,
  Button,
  Input,
  Tag,
  Space,
  Row,
  Col,
  Card,
  message,
  Table,
  Spin,
  Grid,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getIncidents, deleteIncident } from '@/api/incidents';
import { Incident } from '@/types/community/incident';
import { useAuth } from '@/contexts/AuthContext';
import IncidentModal from '@/components/features/community/IncidentModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';

const { Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const API_STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

const incidentStatusColors: Record<Incident['status'], string> = {
  reported: 'orange',
  investigating: 'blue',
  in_progress: 'cyan',
  resolved: 'green',
  closed: 'default'
};

const incidentPriorityColors: Record<Incident['priority'], string> = {
  low: 'green',
  medium: 'gold',
  high: 'orange',
  critical: 'red'
};

const incidentTypeColors: Record<Incident['type'], string> = {
  safety: 'red',
  security: 'purple',
  untidy: 'orange',
  noise: 'blue',
  maintenance: 'geekblue',
  other: 'default'
};

const IncidentReportingPage: React.FC = () => {
  const { t } = useTranslation(['incidentReporting', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const screens = useBreakpoint();

  // State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [deleteError, setDeleteError] = useState<Error | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');

  // Queries
  const { data: incidentsData, isLoading } = useQuery({
    queryKey: ['incidents', firestoreUser?.workspace?.id],
    queryFn: () => getIncidents({ 
      firestoreUser: firestoreUser!,
      pageSize: 100 // Get all incidents for this view
    }),
    enabled: !!firestoreUser?.workspace?.id,
    staleTime: API_STALE_TIME_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  // Mutations
  const deleteIncidentMutation = useMutation({
    mutationFn: (id: string) => deleteIncident(id, firestoreUser!),
    onSuccess: () => {
      message.success(t('incidentReporting:incidentDeleted'));
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      setIsDeleteModalVisible(false);
      setSelectedIncident(null);
      setDeleteError(undefined);
    },
    onError: (error: any) => {
      setDeleteError(error);
    }
  });

  // Memoized values
  const incidentList = useMemo(() => incidentsData?.incidents || [], [incidentsData]);

  // Utility functions
  const handleAddIncident = useCallback(() => {
    setSelectedIncident(null);
    setIsModalOpen(true);
  }, []);

  const handleEditIncident = useCallback((incident: Incident) => {
    setSelectedIncident(incident);
    setIsModalOpen(true);
  }, []);

  const handleViewIncident = useCallback((incident: Incident) => {
    navigate(`/community/incidents/${incident.id}`, { 
      state: { 
        incident,
        from: 'list'
      } 
    });
  }, [navigate]);

  const handleDeleteIncident = useCallback((incident: Incident) => {
    setSelectedIncident(incident);
    setDeleteError(undefined);
    setIsDeleteModalVisible(true);
  }, []);

  const handleModalSuccess = useCallback(() => {
    setIsModalOpen(false);
    setSelectedIncident(null);
  }, []);

  const handleModalCancel = useCallback(() => {
    setIsModalOpen(false);
    setSelectedIncident(null);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (selectedIncident) {
      deleteIncidentMutation.mutate(selectedIncident.id);
    }
  }, [selectedIncident, deleteIncidentMutation]);

  const handleDeleteCancel = useCallback(() => {
    setIsDeleteModalVisible(false);
    setSelectedIncident(null);
    setDeleteError(undefined);
  }, []);

  const handleErrorDismiss = useCallback(() => {
    setDeleteError(undefined);
  }, []);

  // Filter incidents
  const filteredIncidents = useMemo(() => {
    return incidentList.filter(incident => {
      const searchMatch = 
        incident.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        incident.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      return searchMatch;
    });
  }, [incidentList, searchTerm]);

  const columns = [
    {
      title: t('incidentReporting:table.title'),
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: Incident) => (
        <Button type="link" onClick={() => handleViewIncident(record)}>
          {text}
        </Button>
      ),
    },
    {
      title: t('incidentReporting:table.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: t('incidentReporting:table.type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: Incident['type']) => (
        <Tag color={incidentTypeColors[type]}>
          {t(`incidentReporting:types.${type}` as const, type)}
        </Tag>
      ),
    },
    {
      title: t('incidentReporting:table.priority'),
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: Incident['priority']) => (
        <Tag color={incidentPriorityColors[priority]}>
          {t(`incidentReporting:priorities.${priority}` as const, priority)}
        </Tag>
      ),
    },
    {
      title: t('incidentReporting:table.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: Incident['status']) => (
        <Tag color={incidentStatusColors[status]}>
          {t(`incidentReporting:statuses.${status}` as const, status)}
        </Tag>
      ),
    },
    {
      title: t('incidentReporting:table.reportedBy'),
      dataIndex: 'reportedBy',
      key: 'reportedBy',
      render: (reportedBy: Incident['reportedBy']) => reportedBy.name,
    },
    {
      title: t('incidentReporting:table.reportedAt'),
      dataIndex: 'reportedAt',
      key: 'reportedAt',
      render: (date: Date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
      sorter: (a: Incident, b: Incident) => dayjs(a.reportedAt).unix() - dayjs(b.reportedAt).unix(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: t('common:comments'),
      key: 'comments',
      render: (_: any, record: Incident) => (
        <Text type="secondary">
          {record.comments?.length || 0} {t('common:comments')}
        </Text>
      ),
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      render: (_: any, record: Incident) => (
        <Space size="middle" wrap>
          <Button icon={<EditOutlined />} onClick={() => handleEditIncident(record)} size={screens.xs ? 'small' : 'middle'}>
            {!screens.xs && t('common:actions.edit')}
          </Button>
          <Button icon={<DeleteOutlined />} danger onClick={() => handleDeleteIncident(record)} size={screens.xs ? 'small' : 'middle'}>
            {!screens.xs && t('common:actions.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  const renderIncidentCard = (incident: Incident) => (
    <List.Item key={incident.id}>
      <Card
        title={<Text strong>{incident.title}</Text>}
        style={{ width: '100%' }}
        actions={[
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEditIncident(incident)} key="edit"> {t('common:actions.edit')}</Button>,
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteIncident(incident)} key="delete"> {t('common:actions.delete')}</Button>,
        ]}
      >
        <Paragraph ellipsis={{ rows: 3, expandable: true, symbol: t('common:more') }}>
          {incident.description}
        </Paragraph>
        <Space direction="vertical" size="small" style={{ fontSize: '0.85em' }}>
          <Space wrap>
            <Tag color={incidentTypeColors[incident.type]}>
              {t(`incidentReporting:types.${incident.type}` as const, incident.type)}
            </Tag>
            <Tag color={incidentPriorityColors[incident.priority]}>
              {t(`incidentReporting:priorities.${incident.priority}` as const, incident.priority)}
            </Tag>
            <Tag color={incidentStatusColors[incident.status]}>
              {t(`incidentReporting:statuses.${incident.status}` as const, incident.status)}
            </Tag>
          </Space>
          <Text type="secondary">{t('incidentReporting:table.reportedBy')}: {incident.reportedBy.name}</Text>
          <Text type="secondary">{t('incidentReporting:table.reportedAt')}: {dayjs(incident.reportedAt).format('YYYY-MM-DD HH:mm')}</Text>
          <Text type="secondary">{t('common:comments')}: {incident.comments?.length || 0}</Text>
        </Space>
      </Card>
    </List.Item>
  );

  // Loading state
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: screens.xs ? '16px' : '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }} gutter={16}>
        <Col flex={1}>
           <Input.Search
            placeholder={t('incidentReporting:searchPlaceholder')}
            onSearch={setSearchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%' }}
          />
        </Col>
        <Col>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAddIncident}
            block={screens.xs}
          >
            {t('incidentReporting:createIncident')}
          </Button>
        </Col>
      </Row>

      {screens.xs ? (
        <List
          dataSource={filteredIncidents}
          renderItem={renderIncidentCard}
          grid={{ gutter: 16, column: 1 }}
        />
      ) : (
        <Table columns={columns} dataSource={filteredIncidents} rowKey="id" scroll={{ x: 'max-content' }} />
      )}

      {/* Incident Modal */}
      <IncidentModal
        open={isModalOpen}
        incident={selectedIncident}
        onSuccess={handleModalSuccess}
        onCancel={handleModalCancel}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={isDeleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('incidentReporting:confirmDeleteTitle')}
        error={deleteError}
        onErrorDismiss={handleErrorDismiss}
      />
    </div>
  );
}

export default IncidentReportingPage; 